import * as Clipboard from 'expo-clipboard';
import { useLocalSearchParams } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useState } from 'react';
import { Share } from 'react-native';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import Card from '@/components/UI/Card';
import CheckboxItem from '@/components/UI/CheckboxRow';
import Text from '@/components/UI/Text';
import useHaptics from '@/hooks/useHaptics';
import useIsSharingAvailable from '@/hooks/useIsSharingAvailable';
import useTimer from '@/hooks/useTimer';
import getPublicUrl from '@/utils/getPublicUrl';
import isDesktopUserAgent from '@/utils/isDesktopUserAgent';

export default function EventShareScheduleWidget() {
  const { eventId } = useLocalSearchParams<{
    eventId?: string;
  }>();

  const [darkMode, setDarkMode] = useState(false);
  const isSharingAvailable = useIsSharingAvailable();
  const canShare = isSharingAvailable && !isDesktopUserAgent;
  const baseWidgetUrl = `${getPublicUrl()}/widget/v1/schedule/${eventId}`;
  const lightThemeWidgetUrl = `${baseWidgetUrl}?theme=light`;
  const darkThemeWidgetUrl = `${baseWidgetUrl}?theme=dark`;
  const url = darkMode ? darkThemeWidgetUrl : lightThemeWidgetUrl;

  const [copyButton, setCopyButton] = useTimer(
    {
      label: 'Copy',
      disabled: false,
    },
    2000,
  );

  const onShare = useHaptics(async (url: string) => {
    await Share.share({
      message: url,
    });
  });

  const onPreview = useHaptics(async (url: string) => {
    WebBrowser.openBrowserAsync(url, {
      windowFeatures: 'noopener,noreferrer',
      windowName: '_blank',
    });
  });

  const onCopy = useHaptics(async (url: string) => {
    await Clipboard.setStringAsync(url);

    setCopyButton({
      label: 'Copied',
      disabled: true,
    });
  });

  return (
    <Card space="lg" p="lg" w="full">
      <Box
        space="md"
        direction="horizontal"
        justifyContent="space-between"
        flexWrap="wrap"
        alignItems="center"
      >
        <Text size="3xl" flexShrink={1}>
          Schedule Widget
        </Text>
        <Text>
          Embed this link in your website or share it with anyone so they can
          see it right in their browser.
        </Text>
        <Card alignItems="center" direction="vertical" w="full">
          <CheckboxItem
            variant="primary"
            value={!!darkMode}
            onValueChange={() => setDarkMode(!darkMode)}
          >
            <Text>Dark Mode?</Text>
          </CheckboxItem>
          <Box
            w="full"
            p="lg"
            alignItems="center"
            justifyContent="flex-start"
            direction="horizontal"
            space="md"
          >
            <Box flex>
              <Text variant="typographySecondary" numberOfLines={1}>
                {url}
              </Text>
            </Box>

            <ActionButton
              icon="copy-outline"
              variant="secondary"
              label={copyButton.label}
              onPress={() => onCopy(url)}
              disabled={copyButton.disabled}
            />
            {canShare ? (
              <ActionButton
                icon="share-outline"
                variant="secondary"
                label="Share"
                onPress={() => onShare(url)}
              />
            ) : null}

            <ActionButton
              icon="open-outline"
              variant="secondary"
              label="Preview"
              onPress={() => onPreview(url)}
            />
          </Box>
        </Card>
      </Box>
    </Card>
  );
}
